import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
    Box, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Checkbox, 
    FormGroup, 
    FormControlLabel, 
    CircularProgress,
    Alert,
    useMediaQuery
} from '@mui/material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, differenceInCalendarDays } from 'date-fns';
import {it} from 'date-fns/locale/it';
import { useTheme } from '@mui/material/styles';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ActivityBookingDetails } from '../model/ActivityBookingDetails';
import { getActivityBookingDetails } from '../routes/getActivityBookings';

/* ===================== CONFIGURAZIONE CALENDARIO ===================== */

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date: Date) => startOfWeek(date, { locale: it, weekStartsOn: 1 }),
    getDay,
    locales: { 'it': it },
});

/* ===================== COMPONENTE PRINCIPALE ===================== */

interface BookingCalendarProps {
    bookings?: ActivityBookingDetails[];
    onSelectBooking?: (booking: ActivityBookingDetails) => void;
    darkMode?: boolean;
}

const BookingCalendarBlock: React.FC<BookingCalendarProps> = ({ 
    bookings: propsBookings, 
    onSelectBooking, 
    darkMode = false 
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [bookings, setBookings] = useState<ActivityBookingDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [view, setView] = useState<string>(isMobile ? 'agenda' : 'month');
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState({ confirmed: true, cancelled: false });

    // Carica i dati
    useEffect(() => {
        if (propsBookings !== undefined) {
            setBookings(propsBookings);
            setLoadError(null);
            setLoading(false);
            return;
        }

        let active = true;

        (async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const data = await getActivityBookingDetails();
                if (active) setBookings(data);
            } catch (error) {
                if (active) {
                    setLoadError(error instanceof Error ? error.message : 'Impossibile caricare le prenotazioni.');
                }
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [propsBookings]);

    // Configura i colori in base al tema
    const isLight = theme.palette.mode === 'light';
    const mainColor = isLight ? '#424A69' : 'rgba(255, 255, 255, 0.87)';
    const borderAlpha = isLight ? 'rgba(62, 68, 92, 0.4)' : 'rgba(255, 255, 255, 0.2)';
    const hoverBg = isLight ? 'rgba(62, 68, 92, 0.15)' : 'rgba(255, 255, 255, 0.1)';
    const todayBg = isLight ? 'rgba(62, 68, 92, 0.2)' : 'rgba(255, 255, 255, 0.15)';

    // Filtra e formatta gli eventi
    const events = useMemo(() => {
        return bookings
            .filter(b => {
                const status = b.BookingStatus?.toUpperCase();
                if (status === 'CONFIRMED' && !statusFilter.confirmed) return false;
                if (status === 'CANCELLED' && !statusFilter.cancelled) return false;
                return true;
            })
            .map(b => ({
                title: `${b.FirstName} ${b.LastName} (${b.Quantity})`,
                start: new Date(b.StartDateTime),
                end: b.EndDateTime ? new Date(b.EndDateTime) : new Date(new Date(b.StartDateTime).getTime() + 60 * 60 * 1000),
                resource: b,
            }));
    }, [bookings, statusFilter]);

    // Stile degli eventi
    const eventStyleGetter = useCallback((event: any) => {
        const b = event.resource as ActivityBookingDetails;
        const isConfirmed = b.BookingStatus === "CONFIRMED";
        
        return {
            style: {
                backgroundColor: isConfirmed ? "rgba(46, 125, 50, 0.8)" : "rgba(241, 89, 89, 0.8)",
                color: '#fff',
                borderRadius: '4px',
                border: `1px solid ${isConfirmed ? "#2dd135ff" : "#940101ff"}`,
                fontSize: '0.75rem',
                fontWeight: 500,
                padding: '2px 5px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            }
        };
    }, []);

    // Stili del calendario
    const calendarStyles = {
        height: { xs: 'calc(100dvh - 112px)', sm: 650 },
        minHeight: { xs: 520, sm: 650 },
        width: '100%',
        '& .rbc-calendar': {
            fontFamily: 'inherit'
        },

        '& .rbc-toolbar': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            '& .rbc-toolbar-label': {
                order: { xs: -1, sm: 0 },
                flexBasis: { xs: '100%', sm: 'auto' },
                textAlign: { xs: 'left', sm: 'center' },
                fontWeight: 700,
            },
        },

        '& .rbc-btn-group': {
            color: mainColor,
        },

        '& .rbc-toolbar button': {
            color: mainColor,
            border: `1px solid ${borderAlpha}`,
            backgroundColor: 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            padding: '6px 12px',
            borderRadius: '4px',
            fontWeight: 500,

            '&:hover': {
                backgroundColor: hoverBg,
                borderColor: mainColor,
            },

            '&.rbc-active': {
                backgroundColor: hoverBg,
                color: mainColor,
                borderColor: mainColor,
            },
        },

        '& .rbc-header': {
            fontWeight: 700,
            fontSize: '0.85rem',
            padding: { xs: '8px 2px', sm: '12px 0' },
            borderLeft: `1px solid ${borderAlpha}`,
            borderBottom: `2px solid ${borderAlpha}`,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.default,
        },

        '& .rbc-header:first-of-type': {
            borderLeft: 'none',
        },

        '& .rbc-month-view': {
            border: `1px solid ${borderAlpha}`,
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },

        '& .rbc-month-row': {
            borderTop: `1px solid ${borderAlpha}`,
            minHeight: '120px',
        },

        '& .rbc-day-bg': {
            backgroundColor: theme.palette.background.paper,
            borderLeft: `1px solid ${borderAlpha}`,
        },

        '& .rbc-day-bg:first-of-type': {
            borderLeft: 'none',
        },

        '& .rbc-off-range-bg': {
            backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.03)' 
                : 'rgba(0,0,0,0.03)',
            filter: 'brightness(0.9)',
            opacity: 0.7,
        },

        '& .rbc-date-cell': {
            fontSize: { xs: '0.75rem', sm: '0.9rem' },
            padding: { xs: '4px', sm: '8px' },
            fontWeight: 700,
            color: theme.palette.text.primary,
        },

        '& .rbc-event': {
            fontSize: '0.75rem',
            borderRadius: '4px',
            padding: '3px 8px',
            border: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
            margin: '1px 2px',
        },

        '& .rbc-today': {
            backgroundColor: todayBg,
        },

        '& .rbc-date-cell.rbc-now': {
            backgroundColor: 'darkorange',
            color: '#fff !important',
            fontWeight: 900,
            padding: '4px 8px',
            textAlign: 'right',
        },

        '& .rbc-time-view': {
            border: `1px solid ${borderAlpha}`,
            borderRadius: '8px',
            backgroundColor: theme.palette.background.paper,
        },

        '& .rbc-time-header': {
            backgroundColor: theme.palette.background.default,
        },

        '& .rbc-time-header-content': {
            borderLeft: `1px solid ${borderAlpha}`,
        },

        '& .rbc-time-content': {
            borderTop: `2px solid ${borderAlpha}`,
            backgroundColor: theme.palette.background.paper,
        },

        '& .rbc-time-slot': {
            borderTop: `1px solid ${borderAlpha}`,
            color: theme.palette.text.primary,
        },

        '& .rbc-day-slot': {
            borderLeft: `1px solid ${borderAlpha}`,
        },

        '& .rbc-current-time-indicator': {
            backgroundColor: 'darkorange',
            height: '2px',
        },

        '& .rbc-agenda-view': {
            backgroundColor: theme.palette.background.paper,

            '& table.rbc-agenda-table': {
                width: '100% !important',
                tableLayout: 'fixed !important',
                borderCollapse: 'collapse !important',

                '& th': {
                    padding: { xs: '8px 6px !important', sm: '12px 16px !important' },
                    boxSizing: 'border-box !important',
                    backgroundColor: theme.palette.background.default,
                    color: mainColor,
                    fontWeight: 700,
                },

                '& td': {
                    padding: { xs: '8px 6px !important', sm: '12px 16px !important' },
                    overflowWrap: 'anywhere',
                    boxSizing: 'border-box !important',
                    borderRight: `1px solid ${borderAlpha}`,
                    borderBottom: `1px solid ${borderAlpha}`,
                    color: theme.palette.text.primary,
                },
            }
        }
    };

    const daysInMonth = useCallback((date: Date) =>
        differenceInCalendarDays(endOfMonth(date), startOfMonth(date)) + 1
    , []);

    const [agendaLength, setAgendaLength] = useState<number>(30);

    const handleViewChange = useCallback((nextView: string) => {
        setView(nextView);
        if (nextView === 'agenda') {
            const aligned = startOfMonth(currentDate);
            setCurrentDate(aligned);
            setAgendaLength(daysInMonth(aligned));
        }
    }, [currentDate, daysInMonth]);

    const handleNavigate = useCallback((newDate: Date) => {
        if (view === 'agenda') {
            const aligned = startOfMonth(newDate);
            setCurrentDate(aligned);
            setAgendaLength(daysInMonth(aligned));
        } else {
            setCurrentDate(newDate);
        }
    }, [view, daysInMonth]);

    const handleSelectEvent = useCallback((event: any) => {
        if (onSelectBooking) {
            onSelectBooking(event.resource as ActivityBookingDetails);
        }
    }, [onSelectBooking]);

    // Messaggi localizzati
    const messages = {
        next: "Avanti",
        previous: "Indietro",
        today: "Oggi",
        month: "Mese",
        week: "Settimana",
        day: "Giorno",
        agenda: "Agenda",
        date: "Data",
        event: "Evento",
        noEventsInRange: "Nessun evento in questo intervallo"
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 }, width: '100%', minWidth: 0, overflow: 'hidden' }}>
            {loading ? (
                <Box sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center", 
                    height: "calc(100vh - 140px)"
                }}>
                    <CircularProgress />
                </Box>
            ) : loadError ? (
                <Alert severity="error" sx={{ m: { xs: 1, sm: 2 } }}>
                    {loadError}
                </Alert>
            ) : (
                <>
                    <Box sx={calendarStyles as any}>
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            views={isMobile ? ['day', 'agenda'] : ['month', 'week', 'day', 'agenda']}
                            view={view as any}
                            onView={handleViewChange}
                            date={currentDate}
                            onNavigate={handleNavigate}
                            length={view === 'agenda' ? agendaLength : undefined}
                            eventPropGetter={eventStyleGetter}
                            culture="it"
                            messages={messages}
                            popup={false}
                            showAllEvents
                            onSelectEvent={handleSelectEvent}
                        />
                    </Box>

                    {/* Toolbar filtri sotto il calendario */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button 
                            variant="outlined" 
                            size="small"
                            onClick={() => setFiltersOpen(true)}
                        >
                            Filtri
                        </Button>
                    </Box>
                </>
            )}

            <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Filtri Calendario</DialogTitle>
                <DialogContent dividers>
                    <FormGroup>
                        <FormControlLabel 
                            control={<Checkbox checked={statusFilter.confirmed} onChange={e => setStatusFilter({...statusFilter, confirmed: e.target.checked})} />} 
                            label="Mostra Confermate (Verde)" 
                        />
                        <FormControlLabel 
                            control={<Checkbox checked={statusFilter.cancelled} onChange={e => setStatusFilter({...statusFilter, cancelled: e.target.checked})} />} 
                            label="Mostra Cancellate (Rosso)" 
                        />
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFiltersOpen(false)} variant="contained">Applica</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BookingCalendarBlock;
